// ============================================
// ECM Interest & Referral Form Page
// Public form - no authentication required
// ============================================

import React, { useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://54.144.102.147:3001/api';

// Housing options — exact spec text
const HOUSING_OPTIONS = [
  'Staying in a shelter',
  'Living in a motel or hotel temporarily',
  'No fixed or consistent nighttime residence',
  'Staying somewhere not intended for regular housing',
  'Leaving an institution with no stable housing',
  'At risk of losing housing within 30 days',
  'Escaping domestic violence, abuse, stalking, or other unsafe conditions',
  'None of the above',
];

// Condition options — exact spec text ("Other medical or behavioral health conditions" handled separately as showOther)
const CONDITION_OPTIONS = [
  'Diabetes',
  'High Blood Pressure (Hypertension)',
  'Anxiety',
  'Depression',
  'Heart Disease',
  'Coronary Artery Disease',
  'Kidney Disease',
  'Liver Disease',
  'Asthma',
  'Chronic Obstructive Pulmonary Disease (COPD)',
  'Bipolar Disorder',
  'Psychotic Disorders',
  'Substance Use Disorder',
];

interface ReferralFormData {
  fullName: string;
  dateOfBirth: string;
  phone: string;
  email: string;
  county: string;
  preferredLanguage: string;
  preferredLanguageOther: string;
  benefitsIdNumber: string;
  housingInsecurity: string[];       // multi-select
  housingInsecurityOther: string;
  erVisits: string;
  diagnosedConditions: string[];     // multi-select
  diagnosedConditionsOther: string;  // description when "Other" is checked
  additionalInfo: string;
}

const initialFormData: ReferralFormData = {
  fullName: '',
  dateOfBirth: '',
  phone: '',
  email: '',
  county: '',
  preferredLanguage: '',
  preferredLanguageOther: '',
  benefitsIdNumber: '',
  housingInsecurity: [],
  housingInsecurityOther: '',
  erVisits: '',
  diagnosedConditions: [],
  diagnosedConditionsOther: '',
  additionalInfo: '',
};

export const ReferralPage: React.FC = () => {
  const [formData, setFormData] = useState<ReferralFormData>(initialFormData);
  const [errors, setErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const updateField = <K extends keyof ReferralFormData>(field: K, value: ReferralFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    let formatted = digits;
    if (digits.length > 4) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    } else if (digits.length > 2) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    updateField('dateOfBirth', formatted);
  };

  const toggleCheckbox = (field: 'housingInsecurity' | 'diagnosedConditions', value: string) => {
    setFormData((prev) => {
      const arr = prev[field] as string[];
      if (arr.includes(value)) {
        return { ...prev, [field]: arr.filter((v) => v !== value) };
      }
      return { ...prev, [field]: [...arr, value] };
    });
  };

  const validate = (): string[] => {
    const errs: string[] = [];
    if (!formData.fullName.trim()) errs.push('Participant\'s Full Name is required');
    if (!formData.county.trim()) errs.push('County of Residence is required');
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setErrors([]);
    setSubmitting(true);

    // Serialize arrays to comma-separated strings for the backend
    const payload = {
      ...formData,
      housingInsecurity: formData.housingInsecurity.join(', '),
      housingInsecurityOther: formData.housingInsecurityOther,
      diagnosedConditions: formData.diagnosedConditions.join(', '),
      diagnosedConditionsOther: formData.diagnosedConditionsOther,
    };

    try {
      const res = await fetch(`${API_BASE_URL}/referrals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.success) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setErrors(data.errors || ['Submission failed. Please try again.']);
      }
    } catch {
      setErrors(['Network error. Please try again.']);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success screen ──────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen" style={{ background: '#ffffff' }}>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Referral Submitted</h2>
            <p className="text-gray-600 mb-6">
              Thank you for your submission. Our team will review your information and reach out to you soon.
            </p>
            <button
              onClick={() => { setSubmitted(false); setFormData(initialFormData); }}
              className="bg-blue-700 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition font-medium"
            >
              Submit Another Referral
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main form ───────────────────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ background: '#ffffff' }}>
      <div className="max-w-2xl mx-auto px-4 pt-8 pb-12">

        {/* ── Header ── */}
        <div className="flex items-center gap-4 mb-4">
          <img src="/css_logo.png" alt="California Support Services" className="h-14 bg-white rounded-lg p-1" />
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            California Support Services
          </h1>
        </div>

        {/* ── Title block ── */}
        <div className="text-white text-sm leading-relaxed space-y-3 rounded-lg p-6 mb-4" style={{ background: '#1a237e' }}>
          <h2 className="text-lg font-bold text-white mb-2">
            Enhanced Care Management (ECM) Interest &amp; Referral Form
          </h2>
          <p>
            Enhanced Care Management (ECM) is a Medi‑Cal program designed to give individuals extra support when
            health challenges make it difficult to manage care independently. The program helps connect members to
            healthcare services and community resources such as food assistance, clothing, housing support, and other
            essential services.
          </p>
          <p>
            California Support Services proudly provides ECM services to eligible members in the community. If you
            are interested in learning more or receiving support through this program, please complete the information
            below.
          </p>
          <p className="font-semibold">
            Note: Your personal information will only be collected if you choose to provide it.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-2xl shadow border border-gray-100 overflow-hidden">

            {/* Notice */}
            <div className="px-6 pt-6 pb-3 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-800">Participant Information</p>
              <p className="text-xs text-red-600 font-medium mt-1">* Required</p>
            </div>

            {/* Error display */}
            {errors.length > 0 && (
              <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 font-medium text-sm mb-1">Please fix the following:</p>
                <ul className="list-disc list-inside text-red-700 text-sm">
                  {errors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}

            <div className="px-6 pb-6 space-y-8 pt-4">

              {/* 1. Full Name */}
              <QuestionBlock number={1} title="Participant's Full Name" required>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => updateField('fullName', e.target.value)}
                  placeholder="Enter your answer"
                  className="form-input-line"
                />
              </QuestionBlock>

              {/* 2. Date of Birth */}
              <QuestionBlock number={2} title="Date of Birth">
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleDateChange(e.target.value)}
                  placeholder="dd/mm/yyyy"
                  maxLength={10}
                  className="form-input-line"
                />
              </QuestionBlock>

              {/* 3. Contact Phone Number */}
              <QuestionBlock number={3} title="Contact Phone Number">
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.phone}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    updateField('phone', digits);
                  }}
                  placeholder="Enter your answer"
                  maxLength={10}
                  className="form-input-line"
                />
              </QuestionBlock>

              {/* 4. Email Address */}
              <QuestionBlock number={4} title="Email Address">
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="Enter your answer"
                  className="form-input-line"
                />
              </QuestionBlock>

              {/* 5. County of Residence */}
              <QuestionBlock number={5} title="County of Residence" required>
                <input
                  type="text"
                  value={formData.county}
                  onChange={(e) => updateField('county', e.target.value)}
                  placeholder="Enter your answer"
                  className="form-input-line"
                />
              </QuestionBlock>

              {/* 6. Preferred Language */}
              <QuestionBlock number={6} title="Preferred Language">
                <RadioGroup
                  name="preferredLanguage"
                  options={['English', 'Spanish']}
                  value={formData.preferredLanguage}
                  onChange={(val) => updateField('preferredLanguage', val)}
                  otherValue={formData.preferredLanguageOther}
                  onOtherChange={(val) => updateField('preferredLanguageOther', val)}
                  showOther
                  otherLabel="Other"
                />
              </QuestionBlock>

              {/* 7. Medi-Cal BIC Number */}
              <QuestionBlock number={7} title="Medi‑Cal Benefits Identification Card (BIC) Number">
                <div className="rounded-lg mb-4">
                  <img src="/placeholder2.png" alt="Benefits Identification Card" className="rounded-lg max-w-full h-auto" />
                </div>
                <input
                  type="text"
                  value={formData.benefitsIdNumber}
                  onChange={(e) => updateField('benefitsIdNumber', e.target.value)}
                  placeholder="Enter your answer"
                  className="form-input-line"
                />
              </QuestionBlock>

              {/* 8. Housing Situation */}
              <QuestionBlock
                number={8}
                title="Are you currently experiencing any housing instability? (Select all that apply)"
                sectionLabel="Housing Situation"
              >
                <CheckboxGroup
                  name="housingInsecurity"
                  options={HOUSING_OPTIONS}
                  selected={formData.housingInsecurity}
                  onToggle={(val) => toggleCheckbox('housingInsecurity', val)}
                  otherValue={formData.housingInsecurityOther}
                  onOtherChange={(val) => updateField('housingInsecurityOther', val)}
                  showOther
                  otherLabel="Other (please describe)"
                />
              </QuestionBlock>

              {/* 9. ER Visits */}
              <QuestionBlock
                number={9}
                title="Within the past six months, have you visited an emergency room or urgent care center three (3) or more times?"
                sectionLabel="Healthcare Utilization"
              >
                <RadioGroup
                  name="erVisits"
                  options={['Yes', 'No']}
                  value={formData.erVisits}
                  onChange={(val) => updateField('erVisits', val)}
                />
              </QuestionBlock>

              {/* 10. Diagnosed Conditions */}
              <QuestionBlock
                number={10}
                title="Have you ever been diagnosed with any of the following? (Select all that apply)"
                sectionLabel="Health Conditions"
              >
                <CheckboxGroup
                  name="diagnosedConditions"
                  options={CONDITION_OPTIONS}
                  selected={formData.diagnosedConditions}
                  onToggle={(val) => toggleCheckbox('diagnosedConditions', val)}
                  showOther
                  otherLabel="Other medical or behavioral health conditions"
                  otherValue={formData.diagnosedConditionsOther}
                  onOtherChange={(val) => updateField('diagnosedConditionsOther', val)}
                />
              </QuestionBlock>

              {/* 11. Additional Notes */}
              <QuestionBlock
                number={11}
                title="Additional details you would like us to know (optional)."
                subtitle="Examples: caregiver or guardian contact information, specific needs, or preferred time for contact."
                sectionLabel="Additional Notes"
              >
                <textarea
                  value={formData.additionalInfo}
                  onChange={(e) => updateField('additionalInfo', e.target.value)}
                  placeholder="Enter your answer"
                  rows={4}
                  className="form-input-line resize-y"
                />
              </QuestionBlock>

            </div>

            {/* Submit */}
            <div className="px-6 pb-6">
              <button
                type="submit"
                disabled={submitting}
                className="bg-blue-700 text-white px-8 py-3 rounded-lg hover:bg-blue-800 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>

            <div className="px-6 pb-4">
              <p className="text-xs text-gray-400">
                Never give out your password.
              </p>
            </div>

          </div>
        </form>
      </div>

      {/* Inline styles */}
      <style>{`
        .form-input-line {
          width: 100%;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 12px 16px;
          font-size: 14px;
          outline: none;
          background: #f9fafb;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .form-input-line:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
          background: #ffffff;
        }
        .form-input-line::placeholder {
          color: #9ca3af;
        }
      `}</style>
    </div>
  );
};

// ============================================
// Sub-components
// ============================================

const QuestionBlock: React.FC<{
  number: number;
  title: string;
  subtitle?: string;
  required?: boolean;
  sectionLabel?: string;
  children: React.ReactNode;
}> = ({ number, title, subtitle, required, sectionLabel, children }) => (
  <div className="pt-2">
    {sectionLabel && (
      <p className="text-xs font-bold text-blue-800 uppercase tracking-wide mb-2">{sectionLabel}</p>
    )}
    <div className="flex items-start gap-3 mb-3">
      <span className="flex-shrink-0 w-7 h-7 bg-blue-800 text-white text-xs font-bold rounded flex items-center justify-center mt-0.5">
        {number}
      </span>
      <div>
        <h3 className="text-sm font-medium text-gray-900">
          {title}
          {required && <span className="text-red-600 ml-1">*</span>}
        </h3>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
    </div>
    <div className="pl-10">{children}</div>
  </div>
);

// Radio group (single select)
const RadioGroup: React.FC<{
  name: string;
  options: string[];
  value: string;
  onChange: (val: string) => void;
  showOther?: boolean;
  otherLabel?: string;
  otherValue?: string;
  onOtherChange?: (val: string) => void;
}> = ({ name, options, value, onChange, showOther, otherLabel = 'Other', otherValue, onOtherChange }) => {
  const isOtherSelected = value === '__other__';

  return (
    <div className="space-y-2">
      {options.map((option) => (
        <label key={option} className="flex items-center gap-3 cursor-pointer group">
          <input
            type="radio"
            name={name}
            checked={value === option}
            onChange={() => onChange(option)}
            className="w-4 h-4 text-blue-700 border-gray-300 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700 group-hover:text-gray-900">{option}</span>
        </label>
      ))}
      {showOther && (
        <label className="flex items-center gap-3 cursor-pointer group">
          <input
            type="radio"
            name={name}
            checked={isOtherSelected}
            onChange={() => onChange('__other__')}
            className="w-4 h-4 text-blue-700 border-gray-300 focus:ring-blue-500"
          />
          <input
            type="text"
            placeholder={otherLabel}
            value={otherValue || ''}
            onChange={(e) => {
              onChange('__other__');
              onOtherChange?.(e.target.value);
            }}
            onFocus={() => onChange('__other__')}
            className="form-input-line flex-1 max-w-xs"
          />
        </label>
      )}
    </div>
  );
};

// Checkbox group (multi-select)
const CheckboxGroup: React.FC<{
  name: string;
  options: string[];
  selected: string[];
  onToggle: (val: string) => void;
  showOther?: boolean;
  otherLabel?: string;
  otherValue?: string;
  onOtherChange?: (val: string) => void;
}> = ({ options, selected, onToggle, showOther, otherLabel = 'Other (please describe)', otherValue, onOtherChange }) => {
  const isOtherChecked = selected.includes('__other__');

  return (
    <div className="space-y-2">
      {options.map((option) => (
        <label key={option} className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={selected.includes(option)}
            onChange={() => onToggle(option)}
            className="w-4 h-4 mt-0.5 text-blue-700 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700 group-hover:text-gray-900">{option}</span>
        </label>
      ))}
      {showOther && (
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={isOtherChecked}
            onChange={() => onToggle('__other__')}
            className="w-4 h-4 mt-0.5 text-blue-700 border-gray-300 rounded focus:ring-blue-500"
          />
          <div className="flex-1">
            <span className="text-sm text-gray-700 group-hover:text-gray-900">{otherLabel}</span>
            {isOtherChecked && (
              <input
                type="text"
                placeholder="Please describe..."
                value={otherValue || ''}
                onChange={(e) => onOtherChange?.(e.target.value)}
                className="form-input-line mt-2"
              />
            )}
          </div>
        </label>
      )}
    </div>
  );
};

export default ReferralPage;
